import { assertNotEmpty, type Optional } from '@festina-life/core'
import { RawSelect } from '@festina-life/ui/forms'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'
import { type ReactNode, useMemo, useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { isInherited } from '../../uiModels/templateOperations/types'
import type { FieldOrigin } from '../../uiModels/types'
import { InheritedValueCopier } from '../copyInheritedValue/InheritedValueCopier'
import { EditorButtonGroup } from '../editorButtonGroup/EditorButtonGroup'
import { HoverEditButton } from '../hoverEditGroup/HoverEditButton'

type Props = {
  value: Optional<string>
  options?: string[]
  fromOrigin: Optional<FieldOrigin>
  isEditable: boolean
  onChange?: (newValue: Optional<string>) => void | Promise<void>
  path: string
  className?: string
  renderNonEditable?: () => ReactNode
}

export const InheritableSelectEditor = ({
  value,
  options = [],
  renderNonEditable,
  fromOrigin,
  isEditable,
  onChange,
  path,
  className,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const [currentValue, setCurentValue] = useState(value)
  const [isEditing, setIsEditing] = useState(false)

  assertNotEmpty(templateVersion)

  const isFieldInherited = isInherited({ path, origin: fromOrigin })

  const handleCancel = () => {
    setIsEditing(false)
    setCurentValue(value ?? '')
  }

  const handleSave = async () => {
    setIsEditing(false)
    await onChange?.(currentValue)
  }

  const isRenderingInReadMode = !isEditable || isFieldInherited || !isEditing
  const optionsIncludingValue = useMemo(
    () => (!!value && !options.includes(value) ? [value, ...options] : options),
    [value, options],
  )

  return (
    <div
      className={cx(
        'group relative flex flex-row items-center size-full gap-x-2',
        className,
      )}
    >
      {isRenderingInReadMode && !!renderNonEditable ? (
        renderNonEditable()
      ) : (
        <RawSelect
          options={optionsIncludingValue}
          value={currentValue}
          disabled={isRenderingInReadMode}
          onChange={setCurentValue}
          className="w-full"
        />
      )}
      {isFieldInherited && fromOrigin && (
        <InheritedValueCopier
          onCopy={() => onChange?.(value)}
          fromOrigin={fromOrigin}
          fieldPath={path}
        />
      )}
      {!isFieldInherited && !isEditing && isEditable && (
        <div className="invisible absolute right-2 top-1 flex flex-row gap-x-2 group-hover:visible">
          <HoverEditButton
            icon={<Icon name="Pencil" />}
            onClick={() => setIsEditing(true)}
          />
        </div>
      )}
      {isEditing && (
        <EditorButtonGroup
          onCancel={handleCancel}
          onSave={handleSave}
          className="relative!"
        />
      )}
    </div>
  )
}
